/*
  BluFood Warenkorb-Beobachter (bf F2, Basis fuer Phase 4)
  Erkennt jede Aenderung am Warenkorb – egal ob durch das Theme, Qikify oder
  eine andere App – und verteilt den aktuellen Warenkorb als Event:

    document.addEventListener('bf:cart:updated', (e) => e.detail.cart)

  Technik: Anfragen an /cart/add, /cart/change, /cart/update, /cart/clear
  (fetch und XMLHttpRequest) werden beobachtet, danach wird /cart.js gelesen.
  Die Original-Anfragen bleiben unveraendert.

  R61 Gratis-Versand per Rabattcode (z. B. Meeresperlen-Praemie, Willkommenscode):
  Versandrabatte stehen in cart.js nur unter discount_codes (applicable: true), nicht bei den
  Rabatten der Positionen – Versand gibt es erst an der Kasse. shipCodeOf() erkennt sie daran.
  Der Code wird ins Warenkorb-Attribut _bf_ship_code gespiegelt; Liquid (snippets/bf-ship-threshold)
  zeigt damit ueberall "Versand gratis" (Drawer, Hinweisleiste, Produktseite).
  R61b: Shopify meldet auch kundengebundene Codes (Meeresperlen-Praemien) im Warenkorb als gueltig.
  Ob der Versand wirklich gratis ist, entscheidet snippets/bf-ship-code-state; JS fragt das ueber
  sections/bf-ship-state ab (einmal je Code und Seitenaufruf), bevor das Update verteilt wird.
  R62: Creator-Codes (Attribute _bf_creator, _bf_creator_code2) werden ergaenzt, falls sie im Warenkorb
  fehlen (z. B. auf der Creator-Seite bei leerem Warenkorb eingeloest) – je Code hoechstens einmal pro Seitenaufruf.
  R69 Gratis-Zugabe (Theme-Einstellungen "BluFood – Gratis-Zugabe", Daten: snippets/bf-gift-config): Im Aktionszeitraum
  (deutsche Zeit nach Serveruhr) liegt je X € Warenwert automatisch ein Gratis-Artikel im Warenkorb – eigene Position mit der
  Eigenschaft _bf_gift. Kostenlos macht ihn der automatische Shopify-Rabatt "Kaufe X, erhalte Y". Sinkt der Warenwert oder endet
  die Aktion, wird die Menge angepasst bzw. die Position entfernt. Entfernt der Kunde die Zugabe selbst, bleibt sie fuer diese
  Sitzung draussen (sessionStorage bf-gift-off); der Drawer bietet "wieder hinzufuegen" an (giftReadd).
*/

const MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/
const EVENT = 'bf:cart:updated'
export const SHIP_ATTR = '_bf_ship_code'
const root = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
// Original-fetch fuer die Attribut-Abgleichung (loest keinen weiteren Abgleich aus)
const nativeFetch = window.fetch ? window.fetch.bind(window) : null

let timer = null
let lastCart = null
let serverDate = null
let serverOffset = 0 // Serverzeit minus Geraetezeit in ms (aus dem Date-Header)

const low = (v) => String(v == null ? '' : v).trim().toLowerCase()
const shipStates = new Map() // Code (klein) -> 'ok' | 'login' | 'claim'
const creatorTried = new Set() // Creator-Codes, die in diesem Seitenaufruf schon ergaenzt wurden

/** Creator-Codes laut Warenkorb-Attributen (Versand- bzw. Haupt-Code und ggf. Prozent-Code) */
export function creatorCodesOf (cart) {
  const a = (cart && cart.attributes) || {}
  return [a._bf_creator, a._bf_creator_code2].map((c) => String(c || '').trim()).filter(Boolean)
}

/** Eingeloester Versand-Rabattcode im Warenkorb (cart.js-Format) oder '' */
export function shipCodeOf (cart) {
  if (!cart || !Array.isArray(cart.discount_codes) || !cart.item_count) return ''
  const onItems = new Set()
  ;(cart.cart_level_discount_applications || []).forEach((a) => {
    if (a && a.target_type !== 'shipping_line') onItems.add(low(a.title))
  })
  ;(cart.items || []).forEach((item) => {
    ;(item.line_level_discount_allocations || []).forEach((d) => {
      if (d && d.discount_application) onItems.add(low(d.discount_application.title))
    })
  })
  const hit = cart.discount_codes.find((c) => c && c.applicable && c.code && !onItems.has(low(c.code)))
  return hit ? String(hit.code).trim() : ''
}

/** Status des Versand-Codes laut Liquid: 'ok' | 'login' | 'claim' | '' (unbekannt/kein Code) */
export function shipStateOf (cart) {
  const code = shipCodeOf(cart)
  return code ? (shipStates.get(low(code)) || '') : ''
}

/** Code, mit dem der Versand gratis ist: bestaetigter Versand-Rabattcode oder Creator-Aktion "Gratis-Versand" (R24) */
export function freeShipCode (cart) {
  const code = shipCodeOf(cart)
  if (code && shipStates.get(low(code)) === 'ok') return code
  const a = (cart && cart.attributes) || {}
  return (a._bf_creator_offer === 'versand' || a._bf_creator_offer === 'kombi') && a._bf_creator ? String(a._bf_creator) : ''
}

/** R62: fehlende Creator-Codes wieder einloesen (vorhandene Codes bleiben) */
async function ensureCreatorCodes (cart) {
  if (!nativeFetch || !cart || !cart.item_count || !Array.isArray(cart.discount_codes)) return cart
  const have = cart.discount_codes.map((c) => low(c && c.code))
  const missing = creatorCodesOf(cart).filter((c) => !have.includes(low(c)) && !creatorTried.has(low(c)))
  if (!missing.length) return cart
  missing.forEach((c) => creatorTried.add(low(c)))
  try {
    const codes = cart.discount_codes.map((c) => String(c.code)).concat(missing)
    const res = await nativeFetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') })
    })
    if (!res.ok) return cart
    const next = await res.json()
    return next && Array.isArray(next.items) ? next : cart
  } catch (e) {
    return cart
  }
}

/** true, wenn das Attribut _bf_ship_code nicht zum Warenkorb passt */
export function shipAttrStale (cart) {
  if (!cart || !Array.isArray(cart.discount_codes)) return false
  const have = String(((cart.attributes || {})[SHIP_ATTR]) || '').trim()
  return have !== shipCodeOf(cart)
}

async function syncShipAttr (cart) {
  if (!nativeFetch || !shipAttrStale(cart)) return cart
  try {
    const res = await nativeFetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [SHIP_ATTR]: shipCodeOf(cart) } })
    })
    if (!res.ok) return cart
    const next = await res.json()
    return next && Array.isArray(next.items) ? next : cart
  } catch (e) {
    return cart
  }
}

/** R61b: Status des (bereits ins Attribut gespiegelten) Versand-Codes beim Server erfragen */
async function verifyShipCode (cart) {
  const code = shipCodeOf(cart)
  if (!code || shipStates.has(low(code))) return
  if (String(((cart.attributes || {})[SHIP_ATTR]) || '').trim().toLowerCase() !== low(code)) return
  try {
    const res = await fetch(root() + '?sections=bf-ship-state', { credentials: 'same-origin' })
    const data = await res.json()
    const html = data && data['bf-ship-state']
    if (!html) return
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bf-ship-state]')
    if (el && low(el.dataset.code) === low(code) && el.dataset.state) shipStates.set(low(code), el.dataset.state)
  } catch (e) { /* unbekannt = nicht gratis anzeigen */ }
}

/* ---------- R69: Gratis-Zugabe ---------- */

export const GIFT_PROP = '_bf_gift'
const GIFT_OFF = 'bf-gift-off' // Kunde hat die Zugabe entfernt (diese Sitzung nicht wieder hinzufuegen)
const GIFT_LAST = 'bf-gift-last' // zuletzt abgeglichene Menge (erkennt, ob der Kunde sie entfernt hat)
let giftCfg
let giftChain = Promise.resolve()
let giftDirty = false

function ss (key, value) {
  try {
    if (value === undefined) return window.sessionStorage.getItem(key)
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, String(value))
  } catch (e) { /* ohne sessionStorage: keine Abwahl merken */ }
  return null
}

/** Einstellungen aus snippets/bf-gift-config (oder null) */
export function giftConfig () {
  if (giftCfg !== undefined) return giftCfg
  giftCfg = null
  try {
    const el = document.getElementById('bf-gift-config')
    if (el) giftCfg = JSON.parse(el.textContent)
  } catch (e) { giftCfg = null }
  return giftCfg
}

/** Zeitpunkt als Zahl JJJJMMTTHHMM in deutscher Zeit (wie snippets/bf-gift-time) */
export function berlinStamp (date) {
  try {
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(date)
    const get = (t) => (parts.find((x) => x.type === t) || {}).value || '00'
    return Number(get('year') + get('month') + get('day') + get('hour') + get('minute'))
  } catch (e) {
    return 0
  }
}

export function giftActive (cfg = giftConfig()) {
  if (!cfg || !cfg.on || !cfg.variant || !(cfg.step > 0)) return false
  const now = berlinStamp(getServerNow())
  if (!now) return false
  if (cfg.start && now < cfg.start) return false
  if (cfg.end && now > cfg.end) return false
  return true
}

export const isGiftLine = (item) => !!(item && item.properties && item.properties[GIFT_PROP])

/** Stand der Zugabe: Warenwert (Cent), zustehende und vorhandene Menge, Positionen */
export function giftStateOf (cart, cfg = giftConfig()) {
  let eligible = 0
  let have = 0
  const lines = []
  ;((cart && cart.items) || []).forEach((item) => {
    if (isGiftLine(item)) {
      have += item.quantity
      lines.push(item)
      return
    }
    if (item.gift_card) return
    eligible += item.original_line_price || 0
  })
  let want = cfg && cfg.step > 0 ? Math.floor(eligible / cfg.step) : 0
  if (cfg && cfg.max > 0) want = Math.min(want, cfg.max)
  return { eligible, want, have, lines, active: giftActive(cfg), off: ss(GIFT_OFF) === '1' }
}

export const giftOptedOut = () => ss(GIFT_OFF) === '1'

/** "Wieder hinzufuegen" im Drawer: Abwahl aufheben und abgleichen */
export function giftReadd () {
  ss(GIFT_OFF, null)
  ss(GIFT_LAST, 0)
  return fetchCart()
}

async function cartPost (path, body) {
  const res = await nativeFetch(root() + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  return res.ok
}

async function readCart () {
  const res = await nativeFetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
  return res.json()
}

/** Warenkorbseite (Expanse) zeigt Aenderungen erst nach dem Neuladen – hoechstens alle 8 Sekunden */
function reloadCartPage () {
  if (!document.body || !document.body.classList.contains('template-cart')) return
  const last = parseInt(ss('bf-gift-reload'), 10) || 0
  if (Date.now() - last < 8000) return
  ss('bf-gift-reload', Date.now())
  window.location.reload()
}

async function doSyncGift (input) {
  const cfg = giftConfig()
  if (!cfg || !nativeFetch || !input || !Array.isArray(input.items)) return input
  // R71: Immer den aktuellen Stand vom Server lesen. Nach "In den Warenkorb" starten oft zwei Abgleiche kurz
  // nacheinander (Beobachter + Drawer oeffnen). Der zweite hatte den Warenkorb noch von VOR der Zugabe und hat die
  // gerade hinzugefuegte Dose als "vom Kunden entfernt" gedeutet (bf-gift-off) und wieder herausgenommen.
  let cart = input
  try {
    const fresh = await readCart()
    if (fresh && Array.isArray(fresh.items)) cart = fresh
  } catch (e) { /* mit dem uebergebenen Stand weiter */ }
  giftDirty = false
  const st = giftStateOf(cart, cfg)
  if (!st.active && !st.have) return cart
  let off = st.off
  const last = parseInt(ss(GIFT_LAST), 10) || 0
  // Weniger Zugaben als zuletzt abgeglichen, obwohl genug Warenwert da ist -> der Kunde hat sie entfernt
  if (st.active && !off && last > 0 && st.have < last && st.want >= last) {
    ss(GIFT_OFF, '1')
    off = true
  }
  const target = st.active && !off ? st.want : 0
  const first = st.lines[0]
  const extra = st.lines.slice(1)
  if (st.have === target && !extra.length) {
    ss(GIFT_LAST, target)
    return cart
  }
  try {
    for (const line of extra) await cartPost('cart/change.js', { id: line.key, quantity: 0 })
    if (first) await cartPost('cart/change.js', { id: first.key, quantity: target })
    else if (target > 0) await cartPost('cart/add.js', { items: [{ id: Number(cfg.variant), quantity: target, properties: { [GIFT_PROP]: '1' } }] })
    const next = await readCart()
    const ok = next && Array.isArray(next.items)
    // Tatsaechlich vorhandene Menge merken (z. B. wenn nicht genug Bestand da war)
    ss(GIFT_LAST, ok ? giftStateOf(next, cfg).have : 0)
    reloadCartPage()
    return ok ? next : cart
  } catch (e) {
    giftDirty = true
    return cart
  }
}

/** Abgleich nacheinander (nie zwei gleichzeitig, sonst doppelte Zugaben) */
function syncGift (cart) {
  const run = giftChain.then(() => doSyncGift(cart))
  giftChain = run.catch(() => {})
  return run
}

export async function fetchCart () {
  const res = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
  const header = res.headers.get('Date')
  if (header) {
    serverDate = header
    const t = Date.parse(header)
    if (!isNaN(t)) serverOffset = t - Date.now()
  }
  const cart = await syncGift(await syncShipAttr(await ensureCreatorCodes(await res.json())))
  await verifyShipCode(cart)
  lastCart = cart
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { cart } }))
  return cart
}

export function scheduleRefresh (delay = 150) {
  clearTimeout(timer)
  timer = setTimeout(() => { fetchCart().catch(() => {}) }, delay)
}

export function onCartUpdate (callback) {
  document.addEventListener(EVENT, (e) => callback(e.detail.cart))
  if (lastCart) callback(lastCart)
}

export function getLastCart () { return lastCart }
export function getServerDate () { return serverDate }
/** Aktuelle Zeit nach Serveruhr (falls bekannt), sonst Geraeteuhr */
export function getServerNow () { return new Date(Date.now() + serverOffset) }

function isMutation (url) {
  try {
    return MUTATION.test(String(url || ''))
  } catch (e) {
    return false
  }
}

function install () {
  if (window.__bfCartWatch) return
  window.__bfCartWatch = true

  if (window.fetch) {
    const originalFetch = window.fetch
    window.fetch = function (input, init) {
      const promise = originalFetch.apply(this, arguments)
      const url = typeof input === 'string' ? input : (input && input.url)
      if (isMutation(url)) promise.then(() => scheduleRefresh()).catch(() => {})
      return promise
    }
  }

  const XHR = window.XMLHttpRequest
  if (XHR && XHR.prototype) {
    const open = XHR.prototype.open
    const send = XHR.prototype.send
    XHR.prototype.open = function (method, url) {
      this.__bfUrl = url
      return open.apply(this, arguments)
    }
    XHR.prototype.send = function () {
      if (isMutation(this.__bfUrl)) this.addEventListener('loadend', () => scheduleRefresh())
      return send.apply(this, arguments)
    }
  }

  // Theme-eigene Events von Expanse (falls vorhanden) ebenfalls beruecksichtigen
  document.addEventListener('cart:updated', () => scheduleRefresh())
  document.addEventListener('ajaxProduct:added', () => scheduleRefresh())
}

install()

// R69: Laeuft eine Gratis-Zugabe, den Warenkorb einmal abgleichen (z. B. Zugabe zum Aktionsstart nachlegen, nach dem Ende entfernen)
function giftOnLoad () {
  const cfg = giftConfig()
  if (cfg && cfg.on) scheduleRefresh(600)
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', giftOnLoad, { once: true })
else giftOnLoad()

window.BF = window.BF || {}
window.BF.cart = { fetchCart, onCartUpdate, scheduleRefresh, getLastCart, getServerDate, getServerNow, shipCodeOf, shipStateOf, freeShipCode, creatorCodesOf, giftStateOf, giftReadd }
